import { Text, View, ScrollView, RefreshControl, Pressable } from 'react-native';
import React, { useMemo, useState } from 'react';
import { StyleSheet } from 'react-native-unistyles';
import { DateTime } from 'luxon';
import { useRouter } from 'expo-router';
import { ActivityIndicator } from 'react-native-paper';
import { useQueryClient } from '@tanstack/react-query';
import { LegendList } from '@legendapp/list';

import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import Chart from '@/components/Charts/Chart';
import Avatar from '@/components/People/Avatar';
import Callout from '@/components/General/Callout';
import Chip from '@/components/General/Chip';
import CutCard from '@/components/General/CutCard';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import Unicon from '@/components/General/Unicon';
import { Table } from '@/components/General/Table';
import usePlatform from '@/utils/hooks/usePlatform';

// period → p_periods preset for bible_study_report_trends / TrendsChart.
export const TEACHER_TREND_PERIODS = [
    { key: 'day', label: 'Day', periods: 30 },
    { key: 'week', label: 'Week', periods: 12 },
    { key: 'month', label: 'Month', periods: 12 },
    { key: 'quarter', label: 'Quarter', periods: 8 },
    { key: 'year', label: 'Year', periods: 5 },
];

const STATUS_ORDER = ['ACTIVE', 'PAUSED', 'COMPLETED', 'CANCELED'];

// Trailing-window presets for the student-outcomes totals (org-local days,
// resolved in the data layer like teacherDetail's 90-day default).
const OUTCOME_RANGES = [
    { days: 30, label: '30d' },
    { days: 90, label: '90d' },
    { days: 180, label: '6mo' },
    { days: 365, label: '1yr' },
];

// 'yyyy-MM-dd' report days parse as local midnight via luxon — avoids the
// UTC-midnight day shift `new Date('yyyy-MM-dd')` produces west of UTC.
const formatReportDay = (day) => day
    ? DateTime.fromISO(day).toLocaleString({ month: 'short', day: 'numeric', year: 'numeric' })
    : '—';

const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount ?? 0);

const getStatusStyle = (status) => {
    switch (status) {
        case 'ACTIVE': return { text: 'success', background: 'successContainer' };
        case 'CANCELED': return { text: 'error', background: 'errorContainer' };
        case 'COMPLETED': return { text: 'info', background: 'onInfo' };
        case 'PAUSED': return { text: 'warning', background: 'warningContainer' };
        default: return { text: 'textSecondary', background: 'surface' };
    }
};

const StatItem = ({ label, value, testID }) => (
    <View style={styles.statItem} testID={testID}>
        <Text style={styles.statValue}>{value ?? '—'}</Text>
        <Text style={styles.statLabel}>{label}</Text>
    </View>
);

/**
 * Teacher detail surface ("are they effective?") backed by
 * bible_study_report_teacher_detail. Rendered by the Teachers tab on
 * /biblestudies/reports (BibleStudyTeachersPanel) when a single teacher is
 * selected; 'own'-scope callers are preselected to themselves there.
 *
 * @param {string} teacherID - person id of the teacher.
 * @param {'own'|'all'} scope - report scope; 'all' needs report/manage perms.
 * @param {string} [period] - trend period seed for the embedded chart
 *   ('day'|'week'|'month'|'quarter'|'year'). The chart's own in-iframe
 *   toggle owns switching after render; this only seeds chartParams.
 */
const BibleStudyTeacherDetail = ({ teacherID, scope = 'own', period = null }) => {
    const router = useRouter();
    const queryClient = useQueryClient();
    const { isWeb, isLandscape } = usePlatform();

    const activePeriod = period || 'week';
    const periodPreset = TEACHER_TREND_PERIODS.find(p => p.key === activePeriod) || TEACHER_TREND_PERIODS[1];

    const [outcomeDays, setOutcomeDays] = useState(90);

    const { data: detail, isFetching, isLoading } = Query.bibleStudies.teacherDetail({ scope, teacherID });
    const { data: outcomes } = Query.bibleStudies.studentOutcomes({ scope, teacherID, windowDays: outcomeDays });
    const { data: loggedInUser } = Query.people.loggedInUser();
    // Basic profile only for avatar rendering — the RPC's teacher object is
    // the source of truth for name/roster state.
    const { data: person } = Query.people.basicById({ id: teacherID });

    const teacher = detail?.teacher;
    const effectiveness = detail?.effectiveness;
    const studies = detail?.studies || [];
    const students = detail?.studentsTaughtList || [];
    const sessionsPerWeek = detail?.sessionsPerWeek;
    const assignments = detail?.currentAssignments;

    const studiesByStatus = useMemo(() => {
        const grouped = {};
        for (const study of studies) {
            const status = study?.status || 'ACTIVE';
            (grouped[status] = grouped[status] || []).push(study);
        }
        return STATUS_ORDER
            .filter(status => grouped[status]?.length)
            .map(status => ({ status, items: grouped[status] }));
    }, [studies]);

    // compact: analytics hides the in-chart scope toggle + meta line — the
    // teacher detail surface owns that framing around the iframe.
    const chartParams = useMemo(() => ({
        scope,
        teacherID,
        period: activePeriod,
        periods: periodPreset.periods,
        compact: 'true',
    }), [scope, teacherID, activePeriod, periodPreset]);

    const onRefresh = () => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.bibleStudies.teacherDetail({ scope, teacherID }) });
    };

    if (isLoading) {
        return <View style={styles.loadingContainer}><ActivityIndicator size="large" /></View>;
    }

    if (!detail?.teacher) {
        return (
            <View style={styles.loadingContainer}>
                <EmptyListPlaceholder type="404" spacing={20} message="That teacher could not be found. They may have been removed from the roster." />
            </View>
        );
    }

    const percentRegular = effectiveness?.percentRegular;
    const regularTotal = (effectiveness?.studentsRegularNow ?? 0) + (effectiveness?.studentsNotRegularNow ?? 0);
    const outcomeMetrics = outcomes?.metrics;
    // Belt + braces: the server nulls totalContributions without View
    // Contributions, and the tile only renders for callers who pass the
    // client-side permission check too.
    const showContributions = outcomes?.contributionsPermitted === true && !!loggedInUser?.can?.('View Contributions');
    const sessionSummary = sessionsPerWeek?.weekCount > 0
        ? `${sessionsPerWeek.occurredSessions} session${sessionsPerWeek.occurredSessions === 1 ? '' : 's'} in the last ${sessionsPerWeek.weekCount} complete week${sessionsPerWeek.weekCount === 1 ? '' : 's'} (~${sessionsPerWeek.sessionsPerWeek}/week)`
        : null;

    const avatarProfile = person || { id: teacher?.id, firstName: teacher?.firstName, lastName: teacher?.lastName };

    return (
        <ScrollView
            contentContainerStyle={styles.screen}
            keyboardShouldPersistTaps="handled"
            refreshControl={<RefreshControl refreshing={isFetching && !isLoading} onRefresh={onRefresh} />}
        >
            {/* Header */}
            <CutCard accentColor="primary" style={styles.card} contentStyle={styles.cardContent} testID="bible-study-teacher-header">
                <View style={styles.headerRow}>
                    <Avatar size={56} profile={avatarProfile} disableBorder onPress={() => router.push(`/people/${teacherID}`)} />
                    <View style={styles.headerInfo}>
                        <Pressable onPress={() => router.push(`/people/${teacherID}`)}>
                            <Text style={styles.teacherName}>{teacher?.firstName} {teacher?.lastName}</Text>
                        </Pressable>
                        <View style={styles.badgeRow}>
                            {teacher?.rostered
                                ? <Chip colorVariant="groups" icon="book-outline" testID="bible-study-teacher-roster-badge">Rostered</Chip>
                                : <Chip colorVariant="textSecondary" testID="bible-study-teacher-roster-badge">Not on current roster</Chip>}
                            {!!assignments?.primary && (
                                <View style={styles.headerPill('primary')} testID="bible-study-teacher-primary-count">
                                    <Text style={styles.headerPillText('primary')}>{assignments.primary} bible studies</Text>
                                </View>
                            )}
                            {!!assignments?.coTeacher && (
                                <View style={styles.headerPill('textSecondary')} testID="bible-study-teacher-coteacher-count">
                                    <Text style={styles.headerPillText('textSecondary')}>{assignments.coTeacher} co-taught</Text>
                                </View>
                            )}
                        </View>
                    </View>
                </View>
            </CutCard>

            {/* Effectiveness + Studies — same row on wide, stacked when narrow */}
            <View style={styles.cardPair}>
            <CutCard accentColor="primary" style={[styles.card, styles.cardPairItem]} contentStyle={styles.cardContent} testID="bible-study-teacher-effectiveness">
                <Text style={styles.cardTitle}>Student Outcomes</Text>
                <View style={styles.statsRow}>
                    <StatItem label="Students taught" value={effectiveness?.studentsTaught ?? detail?.distinctStudentsTaught} testID="bible-study-teacher-students-taught" />
                    <View style={styles.statDivider} />
                    <StatItem label="Sessions taught" value={detail?.sessionsTaught?.count} testID="bible-study-teacher-sessions-count" />
                </View>

                {regularTotal > 0 && (
                    <View style={styles.regularSection}>
                        <View style={styles.regularHeader}>
                            <Text style={styles.regularPercent}>{percentRegular != null ? `${Math.round(percentRegular)}%` : '—'}</Text>
                            <Text style={styles.regularCaption}>of students taught are regular attenders now</Text>
                        </View>
                        <View style={styles.regularLegend}>
                            <View style={styles.legendItem}>
                                <View style={styles.legendDot('success')} />
                                <Text style={styles.legendText}>{effectiveness?.studentsRegularNow ?? 0} regular now</Text>
                            </View>
                            <View style={styles.legendItem}>
                                <View style={styles.legendDot('textSecondary')} />
                                <Text style={styles.legendText}>{effectiveness?.studentsNotRegularNow ?? 0} not regular</Text>
                            </View>
                        </View>
                    </View>
                )}

                {/* Student totals over a trailing window
                    (bible_study_report_student_outcomes). */}
                {!!outcomes && outcomes.featureEnabled !== false && (
                    <View style={styles.outcomesSection} testID="bible-study-teacher-outcomes">
                        <View style={styles.outcomesHeader}>
                            <Text style={styles.outcomesTitle}>Student totals (last {outcomeDays} days)</Text>
                            <View style={styles.rangeRow}>
                                {OUTCOME_RANGES.map(option => (
                                    <Pressable
                                        key={option.days}
                                        testID={`bible-study-teacher-outcome-range-${option.days}`}
                                        accessibilityRole="button"
                                        accessibilityState={{ selected: outcomeDays === option.days }}
                                        onPress={() => setOutcomeDays(option.days)}
                                        style={styles.rangePill(outcomeDays === option.days)}
                                    >
                                        <Text style={styles.rangePillText(outcomeDays === option.days)}>{option.label}</Text>
                                    </Pressable>
                                ))}
                            </View>
                        </View>
                        <View style={styles.outcomeGrid}>
                            {showContributions && (
                                <View style={styles.outcomeTile} testID="bible-study-teacher-outcome-contributions">
                                    <Text style={styles.outcomeValue}>{formatCurrency(outcomeMetrics?.totalContributions)}</Text>
                                    <Text style={styles.outcomeLabel}>Total contributions</Text>
                                </View>
                            )}
                            <View style={styles.outcomeTile} testID="bible-study-teacher-outcome-spirit">
                                <Text style={styles.outcomeValue}>{outcomeMetrics?.spiritBaptisms ?? '—'}</Text>
                                <Text style={styles.outcomeLabel}>Spirit baptisms</Text>
                            </View>
                            <View style={styles.outcomeTile} testID="bible-study-teacher-outcome-water">
                                <Text style={styles.outcomeValue}>{outcomeMetrics?.waterBaptisms ?? '—'}</Text>
                                <Text style={styles.outcomeLabel}>Water baptisms</Text>
                            </View>
                            <View style={styles.outcomeTile} testID="bible-study-teacher-outcome-attendance">
                                <Text style={styles.outcomeValue}>{outcomeMetrics?.nonBSAttendance ?? '—'}</Text>
                                <Text style={styles.outcomeLabel}>Non-Bible-Study attendance</Text>
                            </View>
                        </View>
                    </View>
                )}

                {regularTotal > 0 && (
                    <Callout
                        type="info"
                        title="Regular attenders"
                        message="A student counts as a regular attender with 6+ attendance records in the last 90 days (non-Bible-Study events). Attendance patterns are descriptive, not causal — many factors affect whether a student attends regularly."
                        testID="bible-study-teacher-effectiveness-note"
                    />
                )}
            </CutCard>

            {/* Studies */}
            <CutCard accentColor="primary" style={[styles.card, styles.cardPairItem]} contentStyle={styles.cardContent} testID="bible-study-teacher-studies">
                <Text style={styles.cardTitle}>Studies ({studies.length})</Text>
                {studiesByStatus.length === 0 && (
                    <Text style={styles.emptyText}>No studies assigned to this teacher.</Text>
                )}
                {/* Capped inner scroll — long rosters scroll inside the card
                    instead of stretching it; nestedScrollEnabled for Android. */}
                <ScrollView style={styles.studiesScroll} nestedScrollEnabled keyboardShouldPersistTaps="handled">
                    {studiesByStatus.map(group => (
                        <View key={group.status} style={styles.studyGroup}>
                            <Text style={styles.studyGroupLabel}>{group.status.charAt(0) + group.status.slice(1).toLowerCase()} ({group.items.length})</Text>
                            {group.items.map(study => {
                                const statusColors = getStatusStyle(study.status);
                                return (
                                    <Pressable
                                        key={study.id}
                                        testID={`bible-study-teacher-study-${study.id}`}
                                        accessibilityRole="button"
                                        onPress={() => router.push(`/biblestudies/${study.id}`)}
                                        style={styles.studyRow}
                                    >
                                        <View style={styles.studyInfo}>
                                            <Text style={styles.studyName} numberOfLines={1}>{study.name}</Text>
                                            <Text style={styles.studyMeta}>
                                                {study.isPrimary ? 'Primary teacher' : 'Co-teacher'}{study.memberCount != null ? ` · ${study.memberCount} student${study.memberCount === 1 ? '' : 's'}` : ''}
                                            </Text>
                                        </View>
                                        <View style={styles.statusPill(statusColors.background, statusColors.text)}>
                                            <Text style={styles.statusPillText(statusColors.text)}>{study.status}</Text>
                                        </View>
                                        <Unicon name="chevron-forward" size={16} colorVariant="textSecondary" />
                                    </Pressable>
                                );
                            })}
                        </View>
                    ))}
                </ScrollView>
            </CutCard>
            </View>

            {/* Sessions — the Chart self-wraps in a CutCard now, so this
                section is a label + chart card instead of a nested card. */}
            <View style={styles.sessionsSection} testID="bible-study-teacher-sessions">
                <Text style={styles.cardTitle}>Sessions</Text>
                {sessionSummary && <Text style={styles.sessionSummary} testID="bible-study-teacher-session-summary">{sessionSummary}</Text>}
                {/* Period switching lives inside the chart's own in-iframe
                    toggle now — the parent only seeds `period`/`periods`. */}
                {/* Chart's own flexBasis is horizontal — needs a row-wrap
                    container like the reports chartGrid to size correctly. */}
                <View style={styles.chartGrid}>
                    <Chart height={isWeb ? 400 : 340} autoHeight chartName="bible-studies/TrendsChart" parameters={chartParams} />
                </View>
            </View>

            {/* Students taught */}
            <CutCard accentColor="primary" style={styles.card} contentStyle={styles.cardContent} testID="bible-study-teacher-students">
                <Text style={styles.cardTitle}>Students Taught ({students.length})</Text>
                {students.length === 0 ? (
                    <Text style={styles.emptyText}>No students were taught in this period.</Text>
                ) : (isWeb || isLandscape) ? (
                    // Rows arrive pre-sorted (status, then sessions desc) —
                    // render in given order, no re-sort.
                    <Table
                        keyExtractor={(item) => item.personID}
                        noBackground
                        data={students}
                        onRowClick={(student) => router.push(`/people/${student.personID}`)}
                        columns={[
                            {
                                accessor: 'lastName', value: 'Student', renderer: (student) => (
                                    <View style={styles.studentCell}>
                                        <Avatar size={28} profile={{ id: student.personID, firstName: student.firstName, lastName: student.lastName }} disableBorder />
                                        <Text style={styles.studentName}>{student.firstName} {student.lastName}</Text>
                                    </View>
                                )
                            },
                            { accessor: 'sessionsAttended', value: 'Sessions' },
                            {
                                accessor: 'lastTaughtDate', value: 'Last taught', renderer: (student) => (
                                    <Text style={styles.cellText}>{formatReportDay(student.lastTaughtDate)}</Text>
                                )
                            },
                            {
                                accessor: 'studyStatus', value: 'Current study', renderer: (student) => {
                                    if (!student.studyStatus) return <Text style={styles.cellText}>—</Text>;
                                    const statusColors = getStatusStyle(student.studyStatus);
                                    return (
                                        <View style={styles.statusPill(statusColors.background, statusColors.text)}>
                                            <Text style={styles.statusPillText(statusColors.text)}>{student.studyStatus}</Text>
                                        </View>
                                    );
                                }
                            },
                            {
                                accessor: 'regularNow', value: 'Regular attender', renderer: (student) => (
                                    <View style={styles.regularBadge(student.regularNow)}>
                                        <Text style={styles.regularBadgeText(student.regularNow)}>{student.regularNow ? 'Yes' : 'No'}</Text>
                                    </View>
                                )
                            },
                        ]}
                    />
                ) : (
                    <LegendList
                        keyExtractor={(item) => item.personID}
                        data={students}
                        estimatedItemSize={56}
                        scrollEnabled={false}
                        renderItem={({ item: student }) => (
                            <Pressable
                                testID={`bible-study-teacher-student-${student.personID}`}
                                accessibilityRole="button"
                                onPress={() => router.push(`/people/${student.personID}`)}
                                style={styles.studentRow}
                            >
                                <Avatar size={36} profile={{ id: student.personID, firstName: student.firstName, lastName: student.lastName }} disableBorder />
                                <View style={styles.studentInfo}>
                                    <Text style={styles.studentName} numberOfLines={1}>{student.firstName} {student.lastName}</Text>
                                    <Text style={styles.studentMeta} numberOfLines={1}>
                                        {student.sessionsAttended} session{student.sessionsAttended === 1 ? '' : 's'}{student.lastTaughtDate ? ` · last taught ${formatReportDay(student.lastTaughtDate)}` : ''}
                                    </Text>
                                </View>
                                {!!student.studyStatus && (
                                    <View style={styles.statusPill(getStatusStyle(student.studyStatus).background, getStatusStyle(student.studyStatus).text)}>
                                        <Text style={styles.statusPillText(getStatusStyle(student.studyStatus).text)}>{student.studyStatus}</Text>
                                    </View>
                                )}
                                <View style={styles.regularBadge(student.regularNow)}>
                                    <Text style={styles.regularBadgeText(student.regularNow)}>{student.regularNow ? 'Yes' : 'No'}</Text>
                                </View>
                            </Pressable>
                        )}
                    />
                )}
            </CutCard>

            <View style={{ paddingBottom: 30 }} />
        </ScrollView>
    );
};

const styles = StyleSheet.create(({ colors, fonts, shadows, roundness }) => ({
    loadingContainer: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
    },
    screen: {
        padding: 16,
        gap: 14,
    },
    cardPair: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 14,
    },
    cardPairItem: {
        flexGrow: 1,
        flexBasis: 400,
    },
    card: {
        padding: 16,
        // CutCard owns surface/radius; the shadow stays here so it converts
        // to a clip-path drop-shadow on web.
        boxShadow: shadows.card,
        _web: {
            willChange: 'transform',
        },
    },
    cardContent: {
        gap: 12,
    },
    cardTitle: {
        fontSize: 15,
        fontFamily: fonts.heading,
        color: colors.text,
    },
    headerRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 14,
    },
    headerInfo: {
        flex: 1,
        gap: 8,
    },
    teacherName: {
        fontSize: 20,
        fontFamily: fonts.bold,
        color: colors.text,
    },
    badgeRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 6,
        alignItems: 'center',
    },
    // Compact count pills next to the teacher name — Chip's flexShrink
    // crushes short count labels, so these are plain non-shrinking pills
    // styled to match the 'Rostered' badge.
    headerPill: (variant) => ({
        paddingVertical: 3,
        paddingHorizontal: 8,
        borderRadius: 8,
        backgroundColor: colors[`${variant}Container`],
        alignSelf: 'flex-start',
    }),
    headerPillText: (variant) => ({
        fontSize: 11,
        fontFamily: fonts.bold,
        color: colors[variant],
    }),
    statsRow: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    statItem: {
        flex: 1,
        alignItems: 'center',
        gap: 2,
    },
    statValue: {
        fontSize: 22,
        fontFamily: fonts.bold,
        color: colors.text,
    },
    statLabel: {
        fontSize: 11,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        textAlign: 'center',
    },
    statDivider: {
        width: 1,
        alignSelf: 'stretch',
        backgroundColor: colors.borderSubtle,
    },
    regularSection: {
        gap: 10,
    },
    regularHeader: {
        flexDirection: 'row',
        alignItems: 'baseline',
        gap: 8,
    },
    regularPercent: {
        fontSize: 26,
        fontFamily: fonts.bold,
        color: colors.success,
    },
    regularCaption: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        flexShrink: 1,
    },
    outcomesSection: {
        gap: 8,
    },
    outcomesHeader: {
        gap: 8,
    },
    outcomesTitle: {
        fontSize: 12,
        fontFamily: fonts.heading,
        color: colors.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: 0.6,
    },
    rangeRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
    },
    rangePill: (selected) => ({
        paddingVertical: 6,
        paddingHorizontal: 14,
        borderRadius: 20,
        backgroundColor: selected ? colors.groupsContainer : colors.surface,
        borderWidth: 1,
        borderColor: selected ? colors.groups : colors.border,
    }),
    rangePillText: (selected) => ({
        fontSize: 12,
        fontFamily: selected ? fonts.bold : fonts.body,
        color: selected ? colors.groups : colors.textSecondary,
    }),
    outcomeGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
    },
    outcomeTile: {
        flexGrow: 1,
        flexBasis: 140,
        gap: 2,
        paddingVertical: 10,
        paddingHorizontal: 12,
        borderRadius: roundness.sm,
        backgroundColor: colors.inputBackground,
    },
    outcomeValue: {
        fontSize: 18,
        fontFamily: fonts.bold,
        color: colors.text,
    },
    outcomeLabel: {
        fontSize: 11,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    studiesScroll: {
        maxHeight: 360,
    },
    regularLegend: {
        flexDirection: 'row',
        gap: 16,
    },
    legendItem: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
    },
    legendDot: (variant) => ({
        width: 8,
        height: 8,
        borderRadius: 4,
        backgroundColor: colors[variant],
    }),
    legendText: {
        fontSize: 12,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    studyGroup: {
        gap: 4,
    },
    studyGroupLabel: {
        fontSize: 12,
        fontFamily: fonts.heading,
        color: colors.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: 0.6,
        paddingBottom: 4,
    },
    studyRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        paddingVertical: 8,
        paddingHorizontal: 4,
        borderRadius: roundness.sm,
        _web: {
            cursor: 'pointer',
            transition: 'background-color 0.15s ease',
            _hover: {
                backgroundColor: colors.hover,
            },
        },
    },
    studyInfo: {
        flex: 1,
        gap: 2,
    },
    studyName: {
        fontSize: 14,
        fontFamily: fonts.heading,
        color: colors.text,
    },
    studyMeta: {
        fontSize: 12,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    // alignSelf: Table cells stretch their children — keeps the pill hugging
    // its label instead of spanning the cell (same pattern as the sessions
    // table status pill).
    statusPill: (bg, text) => ({
        paddingVertical: 3,
        paddingHorizontal: 10,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: colors[text],
        backgroundColor: colors[bg],
        alignSelf: 'flex-start',
    }),
    statusPillText: (color) => ({
        fontSize: 11,
        fontFamily: fonts.bold,
        color: colors[color],
    }),
    sessionsSection: {
        gap: 12,
    },
    chartGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 12,
    },
    sessionSummary: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    cellText: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.text,
    },
    studentCell: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    studentRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        paddingVertical: 8,
    },
    studentInfo: {
        flex: 1,
        gap: 2,
    },
    studentName: {
        fontSize: 14,
        fontFamily: fonts.heading,
        color: colors.text,
    },
    studentMeta: {
        fontSize: 12,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    regularBadge: (regular) => ({
        paddingVertical: 3,
        paddingHorizontal: 10,
        borderRadius: 8,
        backgroundColor: regular ? colors.successContainer : colors.inputBackground,
        alignSelf: 'flex-start',
    }),
    regularBadgeText: (regular) => ({
        fontSize: 11,
        fontFamily: fonts.bold,
        color: regular ? colors.success : colors.textSecondary,
    }),
    emptyText: {
        fontSize: 13,
        fontFamily: fonts.body,
        fontStyle: 'italic',
        color: colors.textSecondary,
    },
}));

export default React.memo(BibleStudyTeacherDetail);
